import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

// Seuils de mouvement au-dela desquels le geste est considere comme un glissement du carrousel
// et non comme un appui. Le tactile est bien plus tolerant : un tap au doigt derive couramment
// de 10 a 20 px, la ou une souris bouge de 1 a 5 px.
const MOVE_THRESHOLD_MOUSE_PX = 12;
const MOVE_THRESHOLD_TOUCH_PX = 25;

class CrossSellComponent extends HTMLElement {
  #downX = null;
  #downY = null;
  #downAt = null;
  #downSlide = null;
  #lastAddAt = 0;
  #watchdog = null;

  constructor() {
    super();

    this.selects = null;
    this.form = null;
    this.formInputs = [];
    this.submitButton = null;
    this.isInCart = false;
    this.isInASlider = false;
    this.productHandle = null;
  }

  connectedCallback() {
    this.selects = this.querySelectorAll('[data-ref="variant-select"]');
    this.form = this.querySelector('[data-ref="add-to-cart-form"]');
    this.formInputs = this.querySelectorAll('[data-ref="product-input"]');
    this.submitButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.isInCart = this.closest('cart-drawer') || this.closest('cart-component') ? true : false;
    this.isInASlider = this.closest('slider-component') ? true : false;
    this.productHandle = this.dataset.productHandle;

    // La carte entiere est cliquable : le <button> est bien plus petit que la carte, et un appui
    // a quelques pixels a cote atterrissait sur .cross-sell__form sans rien declencher.
    // pointerdown/pointerup est le chemin principal car le slider Splide (drag: true) supprime
    // l'evenement `click` des que le geste comporte du mouvement ; `click` reste en repli clavier.
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerCancel);
    this.addEventListener('click', this.#onClick);

    // Le bouton n'est dans aucun <form> : on fige son type pour eviter tout submit implicite.
    if (this.submitButton && !this.submitButton.hasAttribute('type')) {
      this.submitButton.setAttribute('type', 'button');
    }

    this.selects.forEach((select) => {
      select.addEventListener('change', this.#handleSelectChange);
    });

    if (this.isInCart) this.#synchronizeWithCartContent();
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerCancel);
    this.removeEventListener('click', this.#onClick);
    if (this.#watchdog) clearTimeout(this.#watchdog);

    this.selects.forEach((select) => {
      select.removeEventListener('change', this.#handleSelectChange);
    });
  }

  // Zones qui ne doivent jamais declencher l'ajout (choix de variante, liens).
  #isExcludedTarget(target) {
    return !!target.closest?.('select, option, label, a, [data-ref="variant-select"], .cross-sell__select');
  }

  #activeSlide() {
    return this.closest('.splide')?.querySelector('.splide__slide.is-active') || null;
  }

  #onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    this.#downX = e.clientX;
    this.#downY = e.clientY;
    this.#downAt = Date.now();
    this.#downSlide = this.#activeSlide();
  };

  #onPointerCancel = () => {
    this.#downAt = null;
  };

  #onPointerUp = (e) => {
    if (this.#downAt == null) return;
    const moved = Math.hypot(e.clientX - this.#downX, e.clientY - this.#downY);
    const threshold = e.pointerType === 'touch' ? MOVE_THRESHOLD_TOUCH_PX : MOVE_THRESHOLD_MOUSE_PX;
    this.#downAt = null;

    if (this.#isExcludedTarget(e.target)) return;
    if (moved > threshold) return;

    // Si le carrousel a change de slide pendant le geste, c'etait un swipe, pas un appui.
    const slideNow = this.#activeSlide();
    if (this.#downSlide && slideNow && slideNow !== this.#downSlide) return;

    this.#triggerAdd();
  };

  #onClick = (e) => {
    if (this.#isExcludedTarget(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    this.#triggerAdd();
  };

  // Point d'entree unique, deduplique : pointerup et click peuvent arriver pour un meme geste.
  #triggerAdd() {
    if (Date.now() - this.#lastAddAt < 600) return;
    this.#lastAddAt = Date.now();
    this.#submit();
  }

  async #submit() {
    if (!this.submitButton || this.submitButton.disabled) return;
    if (this.submitButton.classList.contains('is-loading')) return;

    this.submitButton.classList.add('is-loading');

    // Garde-fou : si la requete ne repond jamais, on debloque le bouton au lieu de le figer a vie.
    this.#watchdog = setTimeout(() => {
      this.submitButton?.classList.remove('is-loading');
    }, 8000);

    let items = [];

    this.selects.forEach((select) => {
      items.push({
        id: select.value,
        quantity: 1,
      });
    });

    const sections = ['auto-gifts'];
    if (document.querySelector('cart-drawer')) {
      const section_cart_drawer_id = document.querySelector('cart-drawer').dataset.sectionId;
      sections.push(section_cart_drawer_id);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      this.updateCart(data, items.length);

      clearTimeout(this.#watchdog);
      this.submitButton.classList.remove('is-loading');
    } catch (error) {
      console.error('Error:', error);
      clearTimeout(this.#watchdog);
      this.submitButton.classList.remove('is-loading');
    }
  }

  #handleSelectChange = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.submitButton.disabled = true;
    this.updateBundlePrices();

    this.submitButton.disabled = false;

    if (this.dataset.dynamicVariantImage === 'true') {
      this.updateImage();
    }
  };

  updateCart(data, itemsAdded) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    } else {
      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();

      let newItemCount;
      if (currentItemCount != null) {
        newItemCount = currentItemCount + itemsAdded;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'cross-sell',
            sections: data.sections,
          },
        ),
      );
    }
  }

  updateBundlePrices() {
    const priceContainer = this.querySelector('.price-container .price');
    const compareAtPriceContainer = this.querySelector('.price-container .compare-at-price');

    let price = 0;
    let compareAtPrice = 0;

    this.selects.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];

      if (selectedOption != null) {
        price += parseInt(selectedOption.dataset.price);
        compareAtPrice += parseInt(selectedOption.dataset.compareAtPrice) || parseInt(selectedOption.dataset.price);
      } else {
        price += parseInt(select.dataset.price);
        compareAtPrice += parseInt(select.dataset.compareAtPrice) || parseInt(select.dataset.price);
      }
    });

    priceContainer.innerText = formatMoney(price);
    if (compareAtPrice > price) {
      compareAtPriceContainer.innerText = formatMoney(compareAtPrice);
    } else {
      compareAtPriceContainer.innerText = '';
    }
  }

  updateImage() {
    const image = this.querySelector('[data-ref="cross-sell-image"]');
    if (!image) return;

    const selectedOption = this.selects[0].options[this.selects[0].selectedIndex];
    if (!selectedOption.dataset.image) return;

    image.src = selectedOption.dataset.image;
  }

  // Si le cross-sell est dans un panier, et que le produit est dans le panier, on masque le cross-sell
  #synchronizeWithCartContent() {
    let cartItems = null;
    if (this.closest('cart-drawer')) cartItems = this.closest('cart-drawer').querySelectorAll('[data-ref="cart-item"]');
    if (this.closest('cart-component')) cartItems = this.closest('cart-component').querySelectorAll('[data-ref="cart-item"]');

    if (!cartItems) return;

    const closestSlider = this.closest('slider-component');

    cartItems.forEach((cartItem) => {
      if (cartItem.dataset.productHandle === this.productHandle) {
        if (this.isInASlider) {
          closestSlider.querySelectorAll('.splide__slide').forEach((slide, index) => {
            if (slide == this.closest('.splide__slide')) {
              closestSlider.remove(index);
            }
          });
        } else {
          this.classList.add('hidden');
        }
      }
    });
  }
}

if (!customElements.get('cross-sell-component')) {
  customElements.define('cross-sell-component', CrossSellComponent);
}
